var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { Autocomplete, autocompleteClasses, Box, FormControl, InputLabel, OutlinedInput, Popper, styled, } from '@mui/material';
import React from 'react';
import { FixedSizeList } from 'react-window';
import { useCommonWebTranslation } from '../../hooks';
import { TypographyWithOverflow } from './TypographyWithOverflow';
const listboxPadding = 8;
// this is interesting trick to pass some of the control to AutoComplete by forwarding array of parameters as react node,
// that will be attached to custom element.
// taken from MUI example
const renderOption = (props, option, state) => [props, option, state];
const RenderRow = (rowProps) => {
    var _a, _b;
    const { data, index, style } = rowProps;
    const item = data[index];
    // unfolding parameters received from Autocomplete
    // we expect to get from there stuff like event handlers, which will give us interaction handling for free
    const [autoCompleteItemProps, option] = item;
    // as long as react-window uses absolute positioning, we need manually to add padding
    const paddedStyle = Object.assign(Object.assign({}, style), { top: `${parseFloat((_b = (_a = style.top) === null || _a === void 0 ? void 0 : _a.toString()) !== null && _b !== void 0 ? _b : '0') + listboxPadding}px` });
    return (React.createElement(Box, Object.assign({}, autoCompleteItemProps, { style: paddedStyle, role: 'option', tabIndex: '-1', component: 'li', "data-value": option.value }),
        React.createElement(TypographyWithOverflow, { component: 'span', sx: { fontStyle: option.value == '' ? 'italic' : undefined, width: '100%' } }, option.label)));
};
// need this to remove all the extra padding from inner list, rendered by virtualization
const StyledPopper = styled(Popper)({
    [`& .${autocompleteClasses.listbox}`]: {
        boxSizing: 'border-box',
        '& ul': {
            padding: 0,
            margin: 0,
        },
    },
});
// another interesting trick to pass some of the control from AutoComplete component
const OuterElementContext = React.createContext({});
const OuterElementType = React.forwardRef((props, ref) => {
    const outerProps = React.useContext(OuterElementContext);
    return React.createElement("div", Object.assign({ ref: ref }, props, outerProps, { "data-test": 'virtual-list' }));
});
const ListboxComponent = React.forwardRef((props, ref) => {
    const { children } = props, other = __rest(props, ["children"]);
    const itemData = [];
    children.forEach((item) => {
        itemData.push(item);
        itemData.push(...(item.children || []));
    });
    const itemCount = itemData.length;
    // measured manually by comparing list item height in DataTable's filter multi select component
    const itemSize = 36;
    // max height is 8 items size
    const getHeight = () => {
        if (itemCount > 8) {
            return 8 * itemSize;
        }
        return itemCount * itemSize;
    };
    return (React.createElement("div", { ref: ref },
        React.createElement(OuterElementContext.Provider, { value: other },
            React.createElement(FixedSizeList, { itemData: itemData, height: getHeight() + 2 * listboxPadding, width: '100%', outerElementType: OuterElementType, innerElementType: 'ul', itemSize: itemSize, overscanCount: 5, itemCount: itemCount, style: { margin: 0 } }, RenderRow))));
});
export const VirtualizedSelect = React.memo((props) => {
    var _a;
    const { t } = useCommonWebTranslation();
    const emptyItem = { value: '', label: t('dataFilter.none') };
    const renderInput = (params) => (React.createElement(FormControl, { fullWidth: params.fullWidth, variant: 'outlined', size: params.size, error: !!props.error, disabled: params.disabled, sx: {
            '& label.MuiInputLabel-root': { paddingRight: 3 },
        } },
        React.createElement(InputLabel, { htmlFor: params.id, variant: 'outlined', error: !!props.error }, props.config.label),
        React.createElement(OutlinedInput, Object.assign({}, params.InputProps, { inputProps: params.inputProps, id: params.id, label: props.config.label, fullWidth: params.fullWidth, size: params.size, error: !!props.error, disabled: params.disabled }))));
    const onChangeHandler = React.useMemo(() => (_event, selectedItem) => {
        var _a;
        props.onChange((_a = selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.value) !== null && _a !== void 0 ? _a : '');
    }, [props]);
    let items;
    if (!props.config.hideEmptyValue) {
        items = [emptyItem, ...props.config.items];
    }
    else {
        items = props.config.items;
    }
    const value = (_a = items.find((item) => props.value === item.value)) !== null && _a !== void 0 ? _a : null;
    return (React.createElement(Autocomplete, { disableListWrap: true, openOnFocus: true, fullWidth: true, size: 'small', options: items, value: value, PopperComponent: StyledPopper, ListboxComponent: ListboxComponent, renderInput: renderInput, onChange: onChangeHandler, renderOption: renderOption, "data-test": props.config['data-test'] }));
});
